// SPDX-License-Identifier: Elastic-2.0
/**
 * App.tsx — root component.
 *
 * Provider order (outer → inner):
 *  1. ErrorBoundary (T-011) — outermost so it catches errors thrown
 *     anywhere in the tree (including provider init). Renders a sanitized
 *     fallback; never leaks raw error messages.
 *  2. ThemeProvider (T-002) — syncs <html data-theme> before anything
 *     else paints; theme persists across route changes.
 *  3. I18nextProvider (T-003) — explicit DI of the singleton; makes
 *     future tests easier (createInstance() + render with a custom
 *     provider).
 *  4. QueryClientProvider (Group 5) — provides the global TanStack
 *     Query cache to every data hook. Sits below i18n so error toasts
 *     can be localized; sits above ToastProvider + router so any data
 *     consumer in the tree shares one cache.
 *  5. ToastProvider (T-011) — between query client and router so
 *     toasts can be triggered from any route + their copy is localized.
 *  6. RouterProvider (T-004) — innermost; takes over rendering for the
 *     entire app tree. Routes + guards live in src/routing/router.tsx.
 *
 * Side-effects (no provider — runs from main.tsx at module load):
 *  - T-007 auth store + refresh orchestrator (initAuth).
 *  - T-008 WebSocket client (initWebSocket).
 *  - T-029 service worker registration (registerSW) — wired by
 *    `<AppShell>` below so the "update available" toast can use
 *    `useToast` from inside the provider tree.
 *
 * `<InstallPrompt />` (T-029) is rendered alongside the router so the
 * bottom-sheet appears on any route once the visit-count threshold is
 * reached.
 */
import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { QueryClientProvider } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ThemeProvider } from "./theme/ThemeProvider";
import { ErrorBoundary } from "./components/ErrorBoundary/ErrorBoundary";
import { ToastProvider } from "./components/Toast/ToastProvider";
import { useToast } from "./components/Toast/useToast";
import { i18next } from "./i18n";
import { queryClient } from "./api/queryClient";
import { router } from "./routing/router";
import { InstallPrompt } from "./pwa/InstallPrompt";
import { registerSW, reloadForUpdate } from "./pwa/registerSW";

function AppShell(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();

  // Register the SW once on mount. The waiting-SW toast hands the user
  // a Reload action; declining keeps them on the current version until
  // they refresh manually.
  useEffect(() => {
    void registerSW({
      onNeedRefresh: () => {
        toast.show({
          kind: "info",
          title: t("pwa.update_available_title"),
          message: t("pwa.update_available_body"),
          durationMs: 12_000,
          action: {
            label: t("pwa.update_available_reload"),
            onClick: () => {
              void reloadForUpdate();
            },
          },
        });
      },
    });
  }, [toast, t]);

  return (
    <>
      <RouterProvider router={router} />
      <InstallPrompt />
    </>
  );
}

export function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <I18nextProvider i18n={i18next}>
          <QueryClientProvider client={queryClient}>
            <ToastProvider>
              <AppShell />
            </ToastProvider>
          </QueryClientProvider>
        </I18nextProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
